import { useRef, useState } from "react";
import { TKeysOfType } from "../../../../shared/types";
import { Wizard } from "react-use-wizard";
import { ESideDeal } from "../../../../shared/wizard/steps/types";
import getDealServiceSteps from "./steps/stepsArray/getDealServiceSteps";
import { IDataFeatures } from "../../../../shared/wizard/steps/featuresStep";
import AnimatedStep from "../../../../shared/wizard/steps/animatedStep";

import clsx from "clsx";
import styles from "./styles.module.scss";

export interface IFillDataWizardDealService extends IDataFeatures {
  sideDeal: ESideDeal | null;
  isAllParticipantDealInCity: boolean | null;

  complexId: number | null;
  streetId: number | null;
  buildingId: number | null;
  areaInSqM: number | null;
  facilityTypeId: number | null;
}

export type TStringKeysOfIFillDataWizardDealService = TKeysOfType<
  IFillDataWizardDealService,
  string | null
>;

export type TNumberKeysOfIFillDataWizardDealService = TKeysOfType<
  IFillDataWizardDealService,
  number | null | number[]
>;

export type TBooleanKeysOfIFillDataWizardDealService = TKeysOfType<
  IFillDataWizardDealService,
  boolean | null
>;

export type TKeyOfDataWizardDealService = keyof IFillDataWizardDealService;

const IS_PADDING_NARROW = window.innerWidth < 600;

const INITIAL_FILL_DATA_WIZARD = {
  sideDeal: null,
  isAllParticipantDealInCity: null,

  maternityCapital: null,
  encumbrance: null,
  childrenOwn: null,

  complexId: null,
  streetId: null,
  buildingId: null,
  areaInSqM: null,
  facilityTypeId: null,
};

const DealServiceWizard = () => {
  const [fillDataWizard, setFillDataWizard] =
    useState<IFillDataWizardDealService>(INITIAL_FILL_DATA_WIZARD);

  const handleChangeDataString = (
    data: ESideDeal,
    key: TStringKeysOfIFillDataWizardDealService
  ) => {
    setFillDataWizard((prev) => {
      return { ...prev, [key]: data };
    });
  };

  const handleChangeDataBoolean = (
    data: boolean,
    key: TBooleanKeysOfIFillDataWizardDealService
  ) => {
    setFillDataWizard((prev) => {
      return { ...prev, [key]: data };
    });
  };

  const handleChangeDataObject = (
    data: Partial<IFillDataWizardDealService>
  ) => {
    setFillDataWizard((prev) => {
      return { ...prev, ...data };
    });
  };

  const handleResetData = (
    keys: TKeyOfDataWizardDealService[],
    data?: number[]
  ) => {
    if (keys.length === 1) {
      const key = keys[0];
      setFillDataWizard((prev) => {
        return { ...prev, [key]: data ? data : null };
      });
    } else {
      const resetValues = keys.reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, {} as Partial<IFillDataWizardDealService>);
      setFillDataWizard((prevData) => ({ ...prevData, ...resetValues }));
    }
  };

  const isNextStep_estimate = !Boolean(
    fillDataWizard.areaInSqM &&
      fillDataWizard.buildingId &&
      fillDataWizard.streetId &&
      fillDataWizard.facilityTypeId
  );

  const dataFeatures: IDataFeatures = {
    encumbrance: fillDataWizard.encumbrance,
    childrenOwn: fillDataWizard.childrenOwn,
    maternityCapital: fillDataWizard.maternityCapital,
  };

  const dataFormEstimate = {
    streetId: fillDataWizard.streetId,
    buildingId: fillDataWizard.buildingId,
    areaInSqM: fillDataWizard.areaInSqM,
    facilityTypeId: fillDataWizard.facilityTypeId,
  };

  const isNextStep_dataFeatures = Object.values(dataFeatures).some(
    (value) => value === null
  );

  const isNextStep_sideDeal = !Boolean(fillDataWizard.sideDeal);
  const isNextStep_isAllParticipantDealInCity =
    fillDataWizard.isAllParticipantDealInCity === null;

  const dealServiceSteps = getDealServiceSteps(
    fillDataWizard,
    dataFormEstimate,
    dataFeatures,
    handleResetData,
    handleChangeDataString,
    handleChangeDataBoolean,
    handleChangeDataObject,
    {
      isNextStep_sideDeal,
      isNextStep_estimate,
      isNextStep_dataFeatures,
      isNextStep_isAllParticipantDealInCity,
    }
  );

  const previousStep = useRef<number>(0);

  return (
    <Wizard
      wrapper={
        <div className={styles.dealServiceWizard}>
          <div className={styles.dealServiceWizard__container}></div>
        </div>
      }
    >
      {dealServiceSteps.map((step, index) => (
        <AnimatedStep key={index} previousStep={previousStep}>
          <div
            className={clsx(
              styles.dealServiceWizard__card,
              IS_PADDING_NARROW && styles.dealServiceWizard__card_paddingNarrow
            )}
          >
            <div className={styles.dealServiceWizard__title}>Сервис сделки</div>
            <div className={styles.dealServiceWizard__wizard}>{step}</div>
          </div>
        </AnimatedStep>
      ))}
    </Wizard>
  );
};

export default DealServiceWizard;
